<template>
  <BaseSidebar
    v-model="modelValue"
    title="Excepcionalidades"
    description="Veja os pedidos de excepcionalidade desta vaga ou solicite uma nova."
    :icon="mdiLockOpenVariant"
  >
    <template v-if="stationContext" #contextHeader>
      <StationContextCard v-bind="stationContext" />
    </template>

    <div v-if="loading" class="flex justify-center items-center h-full min-h-[300px]">
      <v-progress-circular indeterminate color="primary" size="64" />
    </div>
    <div v-else-if="!exceptionalities.length" class="text-left text-md text-textPrimary">
      <p>
        Não há nenhum pedido de excepcionalidade nessa vaga. Clique no botão abaixo caso deseje
        criar uma.
      </p>
    </div>

    <template v-if="exceptionalities.length > 0">
      <div
        v-for="item in exceptionalities"
        :key="item.id"
        class="bg-white rounded-lg border border-gray-300 p-4 mb-4"
      >
        <p class="font-bold text-sm text-textPrimary mb-2 text-start">Pedido #{{ item.id }}</p>
        <hr class="border-gray-200 mb-3" />

        <div class="grid grid-cols-2 gap-y-2 text-sm text-gray-800 mb-4 text-start">
          <div>
            <p class="font-bold">Solicitante</p>
            <p>{{ item.requester.name }}</p>
          </div>
          <div>
            <p class="font-bold">Data da solicitação</p>
            <p>{{ formatDateToString(new Date(item.requestDate)) }}</p>
          </div>
          <div v-if="item.evaluator">
            <p class="font-bold">Aprovador</p>
            <p>{{ item.evaluator.name }}</p>
          </div>
          <div v-if="item.evaluationDate">
            <p class="font-bold">Data da aprovação</p>
            <p>{{ formatDateToString(new Date(item.evaluationDate)) }}</p>
          </div>
        </div>

        <ExceptionalityProgressTimeline
          v-if="timelinePropsMap.get(item.id)"
          v-bind="timelinePropsMap.get(item.id)!"
          class="my-3"
          :default-expanded="false"
          data-testid="exceptionalities-sidebar-progress-timeline"
        />

        <div class="flex justify-between items-center">
          <div class="flex items-center gap-2">
            <StatusBadge :status="item.status" />
            <span
              v-if="isReversalPending(item, flags.enabledApprovalEngine)"
              class="inline-flex items-center rounded-full border border-warning bg-warning/20 px-2 py-0.5 text-xs font-bold text-yellow-800"
              :data-testid="`exceptionalities-sidebar-reversal-pending-${item.id}`"
            >
              Reversão pendente
            </span>
          </div>

          <OutlineButton
            text="Ver pedido"
            height="32px"
            width="auto"
            class="!text-sm !px-3"
            :data-testid="`exceptionalities-sidebar-view-request-button-${item.id}`"
            @click="handleViewRequest(item)"
          />
        </div>
      </div>
    </template>

    <template #footer>
      <!-- Trava do Pós-Plantão (RN-07 / RF-12): desabilita, nunca esconde —
           mesma decisão dos botões do detalhe do plantão. Um pedido novo faz o
           domínio excepcionalidade virar FAIL, e o Audit Bot não reavalia
           plantão aprovado: a aprovação ficaria desatualizada em silêncio.
           Button nativo disabled não dispara hover, então o tooltip escuta o
           wrapper (activator="parent"). -->
      <span
        v-if="canRequestExceptionality && stationContext?.team !== 'Externo'"
        class="relative inline-flex w-full"
      >
        <FilledButton
          text="Nova solicitação"
          width="100%"
          class="!bg-primary30 mt-6"
          data-testid="exceptionalities-sidebar-new-request-button"
          :disabled="postStationLocked"
          @click="handleRequestNew"
        />
        <v-tooltip
          v-if="postStationLocked"
          activator="parent"
          location="top"
          :text="POST_STATION_LOCK_BANNER_TEXT"
        />
      </span>
    </template>
  </BaseSidebar>

  <SummaryExceptionalitySidebar
    v-show="isActionSidebarOpen"
    :is-open="isActionSidebarOpen"
    :exception-id="exceptionIdSelected"
    :action-type="currentActionType"
    :scheduleId="props.scheduleId"
    :station-context="stationContext"
    :post-station-locked="postStationLocked"
    @update:is-open="isActionSidebarOpen = $event"
    @action-completed="handleActionCompletedGeneric"
  />

  <!-- Voto na reversão multi-etapa pendente (pendingAction === 'REVERSAL'). -->
  <RequestExceptionalitySidebar
    v-if="isReversalSidebarOpen && exceptionIdSelected && props.scheduleId"
    v-model:is-open="isReversalSidebarOpen"
    mode="REVERSAL"
    :exception-id="exceptionIdSelected"
    :schedule-id="props.scheduleId"
    :station-context="stationContext"
    :post-station-locked="postStationLocked"
    @submit-success="handleActionCompletedGeneric"
  />
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { mdiLockOpenVariant } from '@mdi/js'
import { useErrorToast } from '@/composables/useErrorToast'

import BaseSidebar from '@/components/designSystem/Sidebar/BaseSidebar.vue'
import StationContextCard from '@/components/designSystem/Cards/StationContextCard.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import StatusBadge from '@/components/designSystem/Badges/StatusBadge.vue'

import ExceptionService from '@/Network/Services/DoctorStationException'
import {
  ExceptionStatusEnum,
  ExceptionActionType,
  DoctorStationExceptionRequest
} from '@/Network/Types/Requests/DoctorStationExceptionRequests'
import { formatDateToString } from '@/utils/date'
import SummaryExceptionalitySidebar from '@/components/designSystem/Sidebar/SummaryExceptionalitySidebar.vue'
import RequestExceptionalitySidebar from '@/components/designSystem/Sidebar/RequestExceptionalitySidebar.vue'
import { isReversalPending } from '@/utils/exception-reversal'
import ExceptionalityProgressTimeline from '@/modules/company/Approvals/AuditoriumExceptionalities/components/ExceptionalityProgressTimeline.vue'
import { mapApprovalToTimeline, mapReversalToTimeline } from '@/utils/approval-timeline-mapper'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { POST_STATION_LOCK_BANNER_TEXT } from '@/modules/company/PostStation/constants'

const { notifyError } = useErrorToast()
const exceptionService = new ExceptionService()
const permissionsStore = usePermissionsStore()
const { flags } = useFeatureFlags()

export interface StationContextData {
  doctorName: string
  doctorCrm?: string
  scheduleName: string
  positionName: string
  shiftName?: string
  team?: string
  timeRange: string
  dutyType?: string
  value?: string
  formation?: string
  repassMode?: string
  proportionalityRule?: string
  breadcrumb?: string
  allocatedBy?: string
  startDetail?: string
  endDetail?: string
  timezone?: string
}

const props = withDefaults(
  defineProps<{
    isOpen: boolean
    doctorStationId: number | null
    scheduleId: number | null
    stationContext?: StationContextData
    autoOpenExceptionId?: number | null
    /**
     * Trava do Pós-Plantão (RN-07 / RF-12) do plantão que abriu esta sidebar.
     *
     * Chega por PROP, e não por sondagem local, de propósito: esta sidebar só
     * conhece `doctorStationId`/`scheduleId`, e quem já tem o `stationId` e a
     * avaliação carregada é o `stationDetailsModal`. Sondar aqui seria um GET
     * extra do mesmo recurso.
     */
    postStationLocked?: boolean
  }>(),
  { postStationLocked: false }
)

const emit = defineEmits<{
  (e: 'update:isOpen', value: boolean): void
  (e: 'request-new'): void
  (e: 'action-completed-reload-data'): void
}>()

const canRequestExceptionality = computed(() => {
  if (!props.scheduleId) return false
  return permissionsStore.check({
    permission: AuthorizationPermissions.StationExceptionalitiesRequesting,
    resource: {
      objectType: AuthorizationResourcesAndSubjects.Schedule,
      objectId: props.scheduleId
    }
  })
})

const exceptionalities = ref<DoctorStationExceptionRequest[]>([])

const loading = ref(false)
const isActionSidebarOpen = ref(false)
const isReversalSidebarOpen = ref(false)
const exceptionIdSelected = ref<number | null>(null)
const currentActionType = ref<ExceptionActionType>(ExceptionActionType.VIEW)

const timelinePropsMap = computed(() => {
  const map = new Map<number, ReturnType<typeof mapApprovalToTimeline>>()
  for (const item of exceptionalities.value) {
    // Reversão pendente: a timeline relevante é a da reversão, não a da aprovação.
    if (isReversalPending(item, flags.enabledApprovalEngine) && item.reversalInstance) {
      map.set(item.id, mapReversalToTimeline(item.reversalInstance))
    } else if (flags.enabledApprovalEngine && item.approvalInstance) {
      map.set(
        item.id,
        mapApprovalToTimeline({
          approvalInstance: item.approvalInstance,
          requestDate: item.requestDate,
          requesterName: item.requester.name
        })
      )
    }
  }
  return map
})

// Fecha a Summary interna e limpa a seleção. Centralizado porque o fechamento
// chega por dois caminhos: (1) usuário fecha por aqui (setter de `modelValue`);
// (2) pai fecha por prop (`stationDetailsModal` escondido pelo consumidor via
// v-model). O caminho (2) dispara o watch de `isOpen`, que chama este mesmo
// método — sem isto, a `SummaryExceptionalitySidebar` interna ficaria aberta
// após o modal ser escondido, criando uma sidebar "fantasma" sobre a página.
function closeActionSidebar() {
  isActionSidebarOpen.value = false
  isReversalSidebarOpen.value = false
  exceptionIdSelected.value = null
  currentActionType.value = ExceptionActionType.VIEW
}

const modelValue = computed({
  get: () => props.isOpen,
  set: (value) => {
    emit('update:isOpen', value)
    if (!value) closeActionSidebar()
  }
})

async function handleViewRequest(item: DoctorStationExceptionRequest) {
  const { id: exceptionId, status } = item
  exceptionIdSelected.value = exceptionId

  // Reversão multi-etapa aguardando votos vem antes do roteamento por status:
  // o status continua "Aprovado" enquanto a reversão está pendente.
  if (isReversalPending(item, flags.enabledApprovalEngine) && props.scheduleId) {
    isActionSidebarOpen.value = false
    isReversalSidebarOpen.value = true
    return
  }

  if (status === ExceptionStatusEnum.AWAITING_APPROVAL) {
    currentActionType.value = ExceptionActionType.CANCEL
  } else if (status === ExceptionStatusEnum.APPROVED) {
    currentActionType.value = ExceptionActionType.REVERT
  } else if (
    status === ExceptionStatusEnum.REJECTED ||
    status === ExceptionStatusEnum.REVERTED ||
    status === ExceptionStatusEnum.CANCELED
  ) {
    currentActionType.value = ExceptionActionType.VIEW
  } else {
    currentActionType.value = ExceptionActionType.VIEW
    console.warn(`Status desconhecido ou não mapeado: ${status}. Exibindo em modo de visualização.`)
  }

  isActionSidebarOpen.value = true
}

function handleActionCompletedGeneric() {
  isActionSidebarOpen.value = false
  isReversalSidebarOpen.value = false
  fetchExceptionalities()
  emit('action-completed-reload-data')
}

function handleRequestNew() {
  emit('request-new')
}

async function fetchExceptionalities() {
  if (!props.doctorStationId) {
    exceptionalities.value = []
    return
  }

  loading.value = true
  exceptionalities.value = []
  try {
    const response = await exceptionService.getByDoctorStationId(props.doctorStationId)

    exceptionalities.value = response.data

    if (props.isOpen && props.autoOpenExceptionId) {
      const target = exceptionalities.value.find((e) => e.id === props.autoOpenExceptionId)
      if (target && isReversalPending(target, flags.enabledApprovalEngine) && props.scheduleId) {
        exceptionIdSelected.value = target.id
        isReversalSidebarOpen.value = true
      } else if (target) {
        exceptionIdSelected.value = target.id
        currentActionType.value = ExceptionActionType.VIEW
        isActionSidebarOpen.value = true
      }
    }
  } catch (err) {
    console.error('Erro ao buscar excepcionalidades', err)
    notifyError(err, 'Não foi possível carregar os pedidos de excepcionalidade')
    // Semântica implícita: falha no fetch implica estado vazio (array já foi
    // inicializado como `exceptionalities.value = []`). Sem isto, componente
    // exibiria o card genérico "Não há nenhum pedido..." (linha 15).
  } finally {
    loading.value = false
  }
}

watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) {
      fetchExceptionalities()
      return
    }
    // Fechamento programático pelo pai: sem isto a `SummaryExceptionalitySidebar`
    // interna (aberta pelo `autoOpenExceptionId`) ficava aberta, sozinha, sobre a
    // página — a sidebar "fantasma" após aprovar/recusar nas Aprovações.
    closeActionSidebar()
  },
  { immediate: true }
)
</script>
